import React, { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import {
  DepartmentRuleSettings,
  EMPTY_MANUAL,
  ManualEnrollForm,
} from './ContractRenewalSettingsParts';

export default function ContractRenewalSettings({
  apiBase = '/api/admin/employee-contract-renewals',
  companyCode = '',
  onChanged,
  setMessage,
  showRules = true,
  showManual = true,
}) {
  const [rules, setRules] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [candidates, setCandidates] = useState([]);
  const [manual, setManual] = useState(EMPTY_MANUAL);
  const [savingKey, setSavingKey] = useState('');
  const [loading, setLoading] = useState(false);

  const endpoint = useCallback((path = '') => {
    if (!companyCode) return `${apiBase}${path}`;
    const params = new URLSearchParams({ company_code: companyCode });
    return `${apiBase}${path}?${params.toString()}`;
  }, [apiBase, companyCode]);

  const bodyWithCompany = useCallback((body = {}) => (
    companyCode ? { ...body, company_code: companyCode } : body
  ), [companyCode]);

  const loadSettings = useCallback(async () => {
    setLoading(true);
    const [rulesRes, candidatesRes] = await Promise.all([
      apiClient.get(endpoint('/settings')),
      apiClient.get(endpoint('/candidates')),
    ]);
    setLoading(false);
    if (rulesRes.ok && rulesRes.data?.success) {
      const nextRules = rulesRes.data.data || [];
      setRules(nextRules);
      setDrafts(Object.fromEntries(nextRules.map((rule) => [rule.department_name, {
        auto_register: Boolean(rule.auto_register),
        initial_contract_value: rule.initial_contract_value || rule.initial_contract_days || 85,
        initial_contract_unit: rule.initial_contract_unit || 'day',
        renewal_period_value: rule.renewal_period_value || rule.renewal_months || 3,
        renewal_period_unit: rule.renewal_period_unit || 'month',
        probation_period_value: rule.probation_period_value || '',
        probation_period_unit: rule.probation_period_unit || 'day',
      }])));
    }
    if (candidatesRes.ok && candidatesRes.data?.success) {
      setCandidates(candidatesRes.data.data || []);
    }
  }, [endpoint]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  function patchDraft(departmentName, patch) {
    setDrafts((prev) => ({ ...prev, [departmentName]: { ...prev[departmentName], ...patch } }));
  }

  function patchManual(patch) {
    setManual((prev) => ({ ...prev, ...patch }));
  }

  async function saveRule(rule) {
    const draft = drafts[rule.department_name] || {};
    setSavingKey(rule.department_name);
    const res = await apiClient.put(endpoint('/settings'), bodyWithCompany({
      department_name: rule.department_name,
      auto_register: Boolean(draft.auto_register),
      initial_contract_value: Number(draft.initial_contract_value || 85),
      initial_contract_unit: draft.initial_contract_unit || 'day',
      renewal_period_value: Number(draft.renewal_period_value || 3),
      renewal_period_unit: draft.renewal_period_unit || 'month',
      probation_period_value: Number(draft.probation_period_value || 0),
      probation_period_unit: draft.probation_period_unit || 'day',
    }));
    setSavingKey('');
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '부서 규칙 저장에 실패했습니다.');
      return;
    }
    setMessage('부서별 계약 규칙을 저장했습니다.');
    await loadSettings();
    await onChanged?.();
  }

  async function saveManual() {
    setSavingKey('manual');
    const res = await apiClient.post(endpoint('/enrollments'), bodyWithCompany({
      user_id: manual.user_id,
      initial_end_date: manual.initial_end_date,
      probation_period_value: Number(manual.probation_period_value || 0),
      probation_period_unit: manual.probation_period_unit || 'day',
      renewal_period_value: Number(manual.renewal_period_value || 3),
      renewal_period_unit: manual.renewal_period_unit || 'month',
      memo: manual.memo,
    }));
    setSavingKey('');
    if (!res.ok || !res.data?.success) {
      setMessage(res.data?.message || '직원 직접 등록에 실패했습니다.');
      return;
    }
    setMessage('직원을 계약갱신관리 대상에 등록했습니다.');
    setManual(EMPTY_MANUAL);
    await loadSettings();
    await onChanged?.();
  }

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {showRules && (
        <DepartmentRuleSettings
          rules={rules}
          drafts={drafts}
          loading={loading}
          savingKey={savingKey}
          onPatch={patchDraft}
          onSave={saveRule}
        />
      )}

      {showManual && (
        <ManualEnrollForm
          candidates={candidates}
          manual={manual}
          saving={savingKey === 'manual'}
          onManualChange={patchManual}
          onSave={saveManual}
        />
      )}
    </div>
  );
}
